import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type SidebarStore = {
  isCollapsed: boolean;
  isMobileOpen: boolean;
  /**
   * Set by pages that need the full window for editing — the CV builder hides
   * the sidebar once you're actually editing a CV, since Save & Exit is the
   * way back out.
   *
   * Deliberately excluded from persistence: a stale `true` in localStorage
   * would hide the sidebar app-wide after a reload.
   */
  isSidebarHidden: boolean;
  toggle: () => void;
  collapse: () => void;
  expand: () => void;
  openMobile: () => void;
  closeMobile: () => void;
  toggleMobile: () => void;
  setSidebarHidden: (hidden: boolean) => void;
};

export const useSidebarStore = create<SidebarStore>()(
  persist(
    (set) => ({
      isCollapsed: true,
      isMobileOpen: false,
      isSidebarHidden: false,
      toggle: () => set((state) => ({ isCollapsed: !state.isCollapsed })),
      collapse: () => set({ isCollapsed: true }),
      expand: () => set({ isCollapsed: false }),
      openMobile: () => set({ isMobileOpen: true }),
      closeMobile: () => set({ isMobileOpen: false }),
      toggleMobile: () => set((state) => ({ isMobileOpen: !state.isMobileOpen })),
      setSidebarHidden: (hidden: boolean) => set({ isSidebarHidden: hidden }),
    }),
    {
      name: 'sidebar-storage', // Persist sidebar state in localStorage
      // Only the user's own collapse preference is worth remembering. An open
      // mobile drawer or a hidden sidebar are both transient view state that
      // would be wrong to restore on the next visit.
      partialize: (state) => ({ isCollapsed: state.isCollapsed }),
    }
  )
);
